import { useState } from 'react'
import { useSWRConfig } from 'swr'

import { InternalEventType } from '@frame/lib/api/events/types'
import { DirectTask, TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { useRootContext } from '@frame/lib/context'
import { useAutoNavigate } from '@frame/lib/hooks/useAutoNavigate'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useStore } from '@frame/lib/stores/useStore'
import { isSessionInTerminalState } from '@frame/lib/utils/session'

import { IncomeSource } from '../types'

export function useDocumentSubmission() {
  const { mutate } = useSWRConfig()

  const sessionState = useStore((state) => state.session?.state)
  const { actions } = useRootContext()
  const { mutateAndNavigate, redirectToSessionStateRoute } = useAutoNavigate()
  const { handleEvent } = useEventHandler()
  const isMultiEmployer = Boolean(useStore(({ session }) => session?.max_employments && session?.max_employments > 1))

  const [isSubmitting, setIsSubmitting] = useState(false)

  async function createTask() {
    const data = await actions.createDirectTask({
      strategy: TrueworkJsStrategy.DOCUMENTS,
      income_source: IncomeSource.OTHER_INCOME,
    })

    if (!data) {
      throw new Error('Failed to create task')
    }

    return data
  }

  async function completeTask(preparedTask: DirectTask) {
    await actions.completeDirectTask({
      directTaskId: preparedTask.id,
      completeDirectTaskBody: {
        strategy: TrueworkJsStrategy.DOCUMENTS,
      },
      multiEmployer: isMultiEmployer,
    })
  }

  async function submitDocuments({ task, fileIds, cb }: { task?: DirectTask; fileIds: string[]; cb?: () => void }) {
    setIsSubmitting(true)

    if (isSessionInTerminalState(sessionState)) {
      redirectToSessionStateRoute()
      return
    }

    if (task) {
      await actions.prepareTask(task.id)
      await actions.attachFilesToDirectTask(task.id, { file_ids: fileIds })
      await completeTask(task)
    } else {
      const newTask = await createTask()
      await actions.prepareTask(newTask.id)
      await actions.attachFilesToDirectTask(newTask.id, { file_ids: fileIds })
      await completeTask(newTask)
    }

    if (isMultiEmployer) {
      mutate(['all', 'tasks'])
      setIsSubmitting(false)
      mutateAndNavigate()
      handleEvent(InternalEventType.DOCUMENT_FLOW_COMPLETED)
      cb?.()
    } else {
      mutateAndNavigate()
    }
  }

  return { submitDocuments, isSubmitting }
}
